import { globals } from 'config/globals';
import { particles } from 'app/contracts/Particles';
import Config from 'config/settings';
import { LookupType, ParticleLayoutType, RepellType } from 'app/enum/Particles';
import { HomeField } from 'app/views/home/webgl/particles/fields/HomeField';
import HomeFieldConfig from 'app/views/home/webgl/particles/config/HomeFieldConfig';
import { HomeParticle } from 'app/views/home/webgl/particles/fields/HomeParticle';
import DepartmentColours from 'app/views/home/webgl/particles/departments/DepartmentColours';
import DelayHelper from 'utils/timers';
import HomeFieldScreensPixelDataConfig from 'app/views/home/webgl/particles/config/HomeFieldScreensPixelDataConfig';
import { Vector2 } from 'app/contracts/Geometry';
import ComboCounter from 'utils/ComboCounter';
import ParticleCords from 'app/views/home/targets/ParticleCords';
import { Simple } from 'utils/analytics';
import BaseDepartmentScene from 'app/views/home/webgl/particles/departments/BaseDepartmentScene';
import { device } from 'utils/device';

export default class SpacesDepartment extends BaseDepartmentScene {
	private _spacesBoxPerspectiveArea: any;
	private _active: boolean = false;
	private _spacesStageIndex = 6;
	private _spacesStageCounter = 0;
	private _spacesParticles: Array<HomeParticle>;
	private _spaceSineParticles: Array<HomeParticle>;
	private _transitionParticles: Array<HomeParticle>;

	private _allParticles: Array<HomeParticle>;
	private _spacesParticleContainer: PIXI.Sprite;
	private _relectionFilter: PIXI.filters.ReflectionFilter;
	private _filterShape: PIXI.Graphics;

	private _hasBeenStarted: boolean = false;
	private _lastPathIndex: number = 0;

	private _processSine: boolean = false;
	private _sineIndex: number = 0;
	private _sineLookup: Array<particles.ParticleLookupProps>;
	private _screensIsometricGridPositions: Array<Vector2> = [];
	private _comboCounter: ComboCounter = new ComboCounter();


	//x/y are written in _setSquarePaths (frame centre) before use:
	private _pathPhases: Array<{
		lookup: LookupType;
		speedM: number;
		expandM: number;
		doExpandChange: boolean;
		x?: number;
		y?: number;
	}> = [{ lookup: LookupType.CIRCLE, speedM: 0.2, expandM: 0.7, doExpandChange: true }];

	private _spacesBoxStages = [
		{ type: 'Rain', frames: 350 },
		{ type: 'PixelImagesLady', frames: 350 },
		{ type: 'SquarePaths', frames: 300 },
		{ type: 'RandomLerpLines', frames: 550 },

		{ type: 'SineWaveGridNormal', frames: 200 },
		{ type: 'SineWaveGridRandom', frames: 300 },
		{ type: 'SineWaveGridWavey', frames: 300 },

		{ type: 'OrganicLines', frames: 640 },
		{ type: 'OrganicLinesQuick', frames: 360 }





	];

	constructor(
		private _homeField: HomeField,
		private _sceneProps: particles.ParticlesSceneProps,
		private _colourProcessor: DepartmentColours
	) {
		super();
		this._spacesParticles = HomeFieldConfig.getParticlePool(this._homeField.particles as Array<HomeParticle>, 'spaces');

		if (window.innerWidth < Config.mobileBreakpoint) {
			this._spacesParticles = this._spacesParticles.slice(0, HomeFieldConfig.spacesParticlesMobile);
		}

		this._transitionParticles = [];
		this._allParticles = this._spacesParticles.concat(this._transitionParticles);
		this._spacesParticleContainer = this._homeField.getSubContainer('spaces');

		this._spacesParticleContainer.visible = false;

		this._sineLookup = this._sceneProps.lookups.sine.table;

		let layout = this._sceneProps.layouts.isometric;
		this._screensIsometricGridPositions = layout.get(
			this._spacesParticles.length,
			{ x: -0.05, y: 0.2 },
			{ w: 0.04, h: 0.5 }
		);
	}

	public tick() {

		if (!this._active) return;

		this._counter();

		this._positionParticleContainer();

		this._updateMask();
		this._resizeFilterArea();
		this._setReflectionBoundaryRatio();

		if (this._processSine) {
			this._processSineWave();
		}
	}

	public showFromWork() {
		this.stopAsActiveScene();
		this.startAsActiveScene();
	}

	/** User is far enough right to be on spaces scene */
	public startAsActiveScene(reposition: boolean = true) {

		this._spacesStageIndex = 0;
		this._spacesStageCounter = 0;

		this._positionParticleContainer();
		this._updateMask();

		let midIndex = Math.round(this._spacesParticles.length / 2);
		let p1 = this._spacesParticles.slice(0, midIndex);
		let p2 = this._spacesParticles.slice(midIndex);

		p1.forEach(this._setSpacesStart.bind(this));


		DelayHelper.setTimeout(() => {
			globals.skipNormalParticleUpdates = true;
			p2.forEach(this._setSpacesStart.bind(this));
		}, 20);

		DelayHelper.setTimeout(() => {
			globals.skipNormalParticleUpdates = true;

			if (reposition) {


				this._spacesStageCounter = 120;

				this._nextSpacesStage();
			} else {
				this._spacesStageCounter = this._spacesBoxStages[this._spacesStageIndex].frames - 90;

				if (!this._hasBeenStarted) {
					this._nextSpacesStage();
				}
			}

			this._active = true;
			this._hasBeenStarted = true;
		}, 40);

		DelayHelper.setTimeout(() => {
			this._spacesParticleContainer.visible = true;
		}, 80);

		DelayHelper.setTimeout(() => {
			this._createRelectionFilter();
		}, 1000);
	}

	private _setSpacesStart(particle: HomeParticle) {
		particle.spaces.setSpacesStart();
	}

	public stopAsActiveScene() {
		this._active = false;
		this._spacesParticleContainer.visible = false;

		this._spacesParticles.forEach((particle) => {
			particle.deactivateAllBehaviours(false);
			particle.active = false;
		});
	}

	public toWork() {

		this._reduceProcessingForWorkTransition();

		gsap.TweenMax.to(this._spacesParticleContainer, Config.toWorkTime, {
			pixi: { y: -1 * device.innerHeight },
			ease: Config.toWorkEase,
			onComplete: () => {
				this._disableForWork();
			}
		});
	}

	public fromWorkToHome() {
		this._enableForWork();
		this.startAsActiveScene(false);

		gsap.TweenMax.to(this._spacesParticleContainer, Config.toWorkTime, {
			pixi: { y: 0 },
			ease: Config.toWorkEase,
			onComplete: () => {}
		});
	}

	//PRIVATE
	//--------------------------------------------------------------------------------------
	//--------------------------------------------------------------------------------------
	//--------------------------------------------------------------------------------------
	//--------------------------------------------------------------------------------------
	//--------------------------------------------------------------------------------------

	private _positionParticleContainer() {
		let x =
			this._sceneProps.progress.containerX +
			this._sceneProps.zonePositions.spacesParticles.cx * this._sceneProps.sizes.container.w;
		this._spacesParticleContainer.x = x;
		this._sceneProps.mouse.particleContainersX.spaces = x;
	}

	private _updateMask() {
		const spacesMask = this._homeField.stage.spacesMask;

		spacesMask.clear();

		spacesMask.beginFill('#fff000');

		const centerPx = this._sceneProps.mouse.particleContainersX.spaces;

		spacesMask.x = centerPx - this._sceneProps.zonePositions.box.widthPx / 2;
		spacesMask.y = device.innerHeight * this._sceneProps.zonePositions.box.y;
		spacesMask.drawRect(
			0,
			0,
			this._sceneProps.zonePositions.box.widthPx,
			this._sceneProps.zonePositions.box.heightPx
		);
		spacesMask.endFill();
		spacesMask.alpha = 0.4;
	}

	private _createRelectionFilter() {
		if (this._relectionFilter || globals.slowFpsMode) {
			return;
		}

		this._relectionFilter = new PIXI.filters.ReflectionFilter({
			amplitude: [1, 5],
			waveLength: [10, 10],
			alpha: [0.25, 0],
			boundary: 0.5,
			mirror: true
		});

		this._homeField.stage.spacesOutter.filters = [this._relectionFilter];

		//Create a solid area to draw the filters over:
		this._filterShape = new PIXI.Graphics();
		this._filterShape.name = 'Screens Filter Area';
		this._homeField.stage.spacesOutter.addChild(this._filterShape);
		this._filterShape.alpha = 0.00001;

		this._setReflectionBoundaryRatio();
	}

	private _setReflectionBoundaryRatio() {
		let realBoundaryPx =
			this._sceneProps.zonePositions.box.y * device.innerHeight +
			this._sceneProps.zonePositions.box.heightPx;

		if (this._relectionFilter) {
			this._relectionFilter.boundary = realBoundaryPx / device.innerHeight + 0.015;
		}
	}

	private _resizeFilterArea() {
		if (!this._filterShape) {
			return;
		}

		this._filterShape.clear();
		this._filterShape.beginFill('#ff0000');
		this._filterShape.drawRect(0, 0, device.innerWidth, device.innerHeight); // drawRect(x, y, width, height)
		this._filterShape.endFill();
	}

	private _reduceProcessingForWorkTransition() {
		this._active = false;
		this._spacesParticles.forEach((p: HomeParticle) => {
			p.repell.active = false;
		});
	}

	private _disableForWork(): void {
		this._spacesParticleContainer.visible = false;

		this._spacesParticles.forEach((p: HomeParticle) => {
			p.deactivateAllBehaviours(false);
			p.active = false;
		});
	}

	private _enableForWork(): void {
		this._spacesParticleContainer.visible = true;
		this._spacesParticles.forEach((p: HomeParticle) => {
			p.active = true;
		});
	}

	private _setPerspectiveArea() {
		this._spacesBoxPerspectiveArea = {
			center: {
				x: this._sceneProps.zonePositions.box.cx,
				y: this._sceneProps.zonePositions.box.cy
			},
			shift: 0.4,
			w: this._sceneProps.zonePositions.box.w,
			h: this._sceneProps.zonePositions.box.h
		};
	}

	private _totalPasses = 0;

	private _counter(): void {
		this._comboCounter.tick();

		this._spacesStageCounter += (window as any).deltaTime;
		if (this._spacesStageCounter >= this._spacesBoxStages[this._spacesStageIndex].frames) {
			this._spacesStageCounter = 0;

			//Deal with the index change:
			this._spacesStageIndex++;
			if (this._spacesStageIndex >= this._spacesBoxStages.length) {
				this._spacesStageIndex = 0;
				this._totalPasses++;
				Simple.event('home_spaces_full_phase_complete', { total_passes: this._totalPasses });
			}

			this._nextSpacesStage();
		}
	}

	private _reset() {
		this._spacesParticles.forEach(this._resetParticle.bind(this));
	}

	private _resetParticle(p) {
		p.position.followParticle = null;
	}

	private _nextSpacesStage(): void {
		this._comboCounter.clear();
		this._reset();

		this._processSine = false;
		let type: string = this._spacesBoxStages[this._spacesStageIndex].type;

		Simple.event('home_spaces_next_phase', { phase: type });

		if (type === 'Rain') this._setSpacesRain();
		else if (type === 'PerspectiveOrbit') this._setSpacesPerspectiveOrbit();
		else if (type === 'WhoozyTargets') this._setWhoozyTargets();
		else if (type === 'SquarePaths') this._setSquarePaths();
		else if (type === 'RandomLerpLines') this._randomLerpLines();
		else if (type === 'PixelImagesLady') this._pixelImageLady();
		else if (type === 'PixelImagesWave') this._pixelWave();
		else if (type === 'PixelImagesLeg') this._pixelImageLeg();
		else if (type === 'PixelImagesMoth') this._pixelMoth();
		else if (type === 'SineWaveGridRandom') this._sineWaveGridRandom();
		else if (type === 'SineWaveGridNormal') this._sineWaveGridNormal();
		else if (type === 'SineWaveGridWavey') this._sineWaveGridWavey();
		else if (type === 'LineDropThenPhysicsBurst') this._lineDropThenPhysicsBurst();
		else if (type === 'PerspectiveGrid') this._setSpacesPerspectiveGrid();
		else if (type === 'LeftDrop') this._setSpacesLeftDrop();
		else if (type === 'BurstUp') this._setSpacesBurstUp();
		else if (type === 'OrganicLines') this._organicLines();
		else if (type === 'OrganicLinesQuick') this._organicLinesQuick();
		else if (type === 'BasicGrid') this._spacesBasicGrid();

		this._colourProcessor.changeSpacesColour(this._allParticles);
	}

	private _setSpacesPerspectiveGrid(): void {
		this._setPerspectiveArea();

		let particleIndex = 0;

		//Do 10 levels of depth
		let depthItems = 8;
		let cols = 9;
		let rows = 7;
		let colSpacing = this._spacesBoxPerspectiveArea.h / (cols - 1);
		let rowSpacing = this._spacesBoxPerspectiveArea.w / (rows - 1);
		let startX = -1 * (this._sceneProps.zonePositions.box.w / 2);
		let startY = this._spacesBoxPerspectiveArea.center.y - this._spacesBoxPerspectiveArea.h / 2;

		for (let depth = 1; depth <= depthItems; depth++) {
			if (depth >= 1) {
				let y = startY;
				let x = startX;
				let depths = [null, 1, 2, 3, 4, 5, 6, 7, 8, 9];

				for (let i = 0; i < cols; i++) {
					for (let j = 0; j < rows; j++) {
						let particle = this._spacesParticles[particleIndex];

						particle.spaces.spacesPerspectiveGrid(
							depths[depth],
							{ x: x, y: y },
							this._spacesBoxPerspectiveArea,
							i,
							j,
							false
						);
						x += rowSpacing;
						particleIndex++;
					}

					y += colSpacing;
					x = startX;
				}
			}
		}

		//Hide the reset of the spaces particles:
		for (let i = particleIndex; i < this._spacesParticles.length; i++) {
			this._spacesParticles[i].hideMe();
		}
	}


	private _setSpacesPerspectiveOrbit(): void {
		this._setPerspectiveArea();

		this._spacesParticles.forEach((particle) => {
			let centerX = -1 * (this._sceneProps.zonePositions.box.w / 2);
			let centerY = this._spacesBoxPerspectiveArea.center.y;
			particle.spaces.setPerspectiveOrbit(this._spacesBoxPerspectiveArea, { x: centerX, y: centerY });
		});
	}

	private _setWhoozyTargets(): void {
		this._spacesParticles.forEach((particle) => {
			particle.spaces.whoozyTargets();
		});
	}

	private _setSquarePaths(): void {
		const config = this._pathPhases[this._lastPathIndex];
		const frame = this._getFrameSizes();

		config.x = frame.xc;
		config.y = frame.yc;
		this._spacesParticles.forEach((particle) => {
			particle.spaces.squarePaths(config, frame);
		});

		this._lastPathIndex++;
		if (this._lastPathIndex > this._pathPhases.length - 1) {
			this._lastPathIndex = 0;
		}
	}

	private _randomLerpLines() {
		let frame = this._getFrameSizes();

		this._spacesParticles.forEach((particle) => {
			particle.spaces.randomLerpLines(frame);
		});
	}

	private _pixelImageLady() {
		let item = HomeFieldScreensPixelDataConfig.getList()[0];
		let particleAtCount = device.innerWidth < Config.mobileBreakpoint ? 4 : 2;
		this._pixelImages(item, particleAtCount, 0.24, 1.2, 1.2, 0.13, 0.18);
	}

	private _pixelImageLeg() {
		let item = HomeFieldScreensPixelDataConfig.getList()[1];
		this._pixelImages(item, 1, 0.05, 0.4, 0.9, -0.28, -0.05);
	}

	private _pixelMoth() {
		let item = HomeFieldScreensPixelDataConfig.getList()[2];
		this._pixelImages(item, 1, 0.04, 0.5, 0.7, -0.25, -0.15);
	}

	private _pixelWave() {
		let item = HomeFieldScreensPixelDataConfig.getList()[3];
		this._pixelImages(item, 4, 0.06, 1.2, 1.2, 0.13, 0.18);
	}

	/** Pass `out` to write into an existing object (avoids a per-frame
	 *  allocation on the sine hot path); otherwise returns a fresh object. */
	private _getFrameSizes(out?) {



		const w = ParticleCords.xPxToRel(this._sceneProps.zonePositions.box.widthPx, this._sceneProps);
		const frameX1 = -1 * (w / 2);
		const frameX2 = w / 2;
		const frameWidth = frameX2 - frameX1;
		const frameXC = frameX1 + frameWidth / 2;

		const frameY1 = this._sceneProps.zonePositions.box.y;
		const frameY2 = this._sceneProps.zonePositions.box.y + this._sceneProps.zonePositions.box.h;
		const frameHeight = frameY2 - frameY1;
		const frameYC = frameY1 + frameHeight / 2;


		const sizes = out || ({} as any);

		sizes.x1 = frameX1;
		sizes.x2 = frameX2;
		sizes.width = frameWidth;
		sizes.xc = frameXC;
		sizes.y1 = frameY1;
		sizes.y2 = frameY2;
		sizes.height = frameHeight;
		sizes.yc = frameYC;

		return sizes;
	}

	private _pixelImages(
		item,
		particleAtCount = 2,
		rscale = 0.18,
		wCorrect = 1.2,
		yCorrect = 1.2,
		indexXP = 0.13,
		indexYP = 0.18
	) {
		const frame = this._getFrameSizes();

		const frameX1 = frame.x1;
		const frameWidth = frame.width;

		const frameY1 = frame.y1;
		const frameHeight = frame.height;

		const indentX = -1 * (frameWidth * indexXP);
		const indentY = -1 * (frameHeight * indexYP);


		let count = 0;
		let pIndexCount = 0;

		for (let i = 0; i < item.pixelData.length; i++) {
			count++;

			if (count >= particleAtCount && this._spacesParticles[pIndexCount]) {
				count = 0;

				let particle = this._spacesParticles[pIndexCount];
				pIndexCount++;

				particle.deactivateAllBehaviours(true);
				particle.particlePhysics.reset();

				particle.activate();
				particle.particleProps.sprite.blendMode = 'add';
				Math.random() > 0.2 ? 0.1 : 0.2;
				let baseScale = item.pixelData[i].radius * rscale;
				particle.animatedProps.hardSetScale(baseScale);
				particle.mouseParallax.active = false;
				particle.animatedProps.alpha = particle.animatedProps.targetAlpha = 0.8 + Math.random() * 0.2;

				let x = frameX1 + indentX + item.pixelData[i].x * (frameWidth * wCorrect);
				let y = frameY1 + indentY + item.pixelData[i].y * (frameHeight * yCorrect);


				particle.animatedProps.setVectorStyleMovement(false);
				particle.particlePhysics.active = true;
				particle.particlePhysics.lerpToPosition({ x: x, y: y }, 900 + Math.random() * 1500, 4);
				particle.particlePhysics.onTargetReached = () => {
					particle.repell.active = true;
					particle.animatedProps.setVectorStyleMovement(true);
					particle.particlePhysics.active = false;
				};

				particle.repell.active = false;
				particle.repell.type = RepellType.PUSH;
				particle.repell.target = this._sceneProps.targets.spacesMouse;
				particle.repell.radius = 150;
				particle.repell.randomisePower(30, 100);

				particle.counter.onTrigger = () => {
					particle.animatedProps.setVectorStyleMovement(false);
					particle.counter.active = false;
					particle.particlePhysics.active = true;
					particle.animatedProps.setEase({ alpha: 50 });
					particle.animatedProps.targetAlpha = 0;
					particle.particlePhysics.lerpToPosition(
						{ x: x, y: y - Math.random() * frameHeight },
						800 + Math.random() * 1500
					);
				};

				particle.counter.triggerAtFrames = (1000 + Math.random() * 40000) / 16;
				particle.counter.start();
				particle.counter.active = true;

				particle.animatedProps.startBreathing(
					baseScale,
					0.01 * baseScale,
					0.4 * baseScale,
					[-1, 1],
					Math.random() * 0.4 * baseScale,
					Math.random() < 0.5 ? -1 : 1
				);

				particle.animatedProps.setEase({
					x: 1,
					y: 1
				});

			}
		}

		const freeParticles = this._spacesParticles.slice(pIndexCount);

		freeParticles.forEach((p) => p.hide());

	}

	private _sineWaveGridRandom(): void {
		this._sineType = 'random';

		this._xIncrease = 5;
		this._maxSineX = 10;
		this._minSineX = -10;
		this._sineDirection = 1;
		this._sineYChange = 0.13;
		this._sinePlaySeed = 2.5;

		this._startSine();
	}

	private _sineWaveGridNormal(): void {
		this._sineType = 'normal';

		this._xIncrease = 5;
		this._maxSineX = 10;
		this._minSineX = -10;
		this._sineDirection = 1;
		this._sineYChange = 0.2;
		this._sinePlaySeed = 2.5;

		this._startSine();
	}

	private _sineWaveGridWavey(): void {
		this._sineType = 'wavey';
		this._startSine();

		this._xIncrease = 5;
		this._maxSineX = 25;
		this._minSineX = -25;
		this._sineDirection = 1;
		this._sineYChange = 0.13;
		this._sinePlaySeed = 3;
	}

	private _startSine() {
		this._sineLookup = this._sceneProps.lookups.sine.table;
		this._processSine = true;

		this._spaceSineParticles = this._spacesParticles.slice(0, this._screensIsometricGridPositions.length);
		const freeParticles = this._spacesParticles.slice(this._spaceSineParticles.length);

		this._spaceSineParticles.forEach((particle, index) => {
			particle.deactivateAllBehaviours(true);

			particle.activate();

			particle.animatedProps.setVectorStyleMovement(false);
			particle.animatedProps.setEase({ y: particle.random(10, 20) });
			particle.sineSeed = particle.random(0.4, 0.8);

			particle.animatedProps.targetAlpha = 0.35 + Math.random() * 0.35;

			particle.sineSeed = 5;

			let baseScale = 0.15;
			particle.activate();
			particle.position.active = true;
			particle.animatedProps.hardSetScale(baseScale);

			particle.repell.active = true;
			particle.repell.target = this._sceneProps.targets.spacesMouse;
			particle.repell.type = RepellType.PUSH;
			particle.repell.testAtCount = 1;
			//Allow for 150px circle and very tight ring:
			particle.repell.radius = 200;
			particle.repell.randomisePower(100, 200);

		});

		freeParticles.forEach((particle, index) => {
			particle.hide();
		});
	}

	private _setSpacesLeftDrop(): void {
		this._spacesParticles.forEach((particle) => {
			particle.spaces.leftDrop();
		});
	}

	private _setSpacesBurstUp(): void {
		this._spacesParticles.forEach((particle) => {
			particle.spaces.burstUp();
		});
	}

	private _setSpacesRain(): void {
		globals.skipNormalParticleUpdates = true;

		let midIndex = Math.round(this._spacesParticles.length / 2);
		let p1 = this._spacesParticles.slice(0, midIndex);
		let p2 = this._spacesParticles.slice(midIndex);

		p1.forEach(this._rainParticle.bind(this));

		DelayHelper.setTimeout(() => {
			globals.skipNormalParticleUpdates = true;
			p2.forEach(this._rainParticle.bind(this));
		}, 20);
	}

	private _rainParticle(p: HomeParticle) {
		p.spaces.rain();
	}

	private _organicLinesQuick() {
		let totalTargets = 5;
		let targetParticles = this._spacesParticles.slice(0, totalTargets);
		let swarmParticles = this._spacesParticles.slice(totalTargets);

		let sizes = this._getFrameSizes();

		let followRadiusMax = 0.025;
		let mobileScaleBumpMax = 0.05;
		let r = 1 - Math.min(1, device.innerWidth / 1000);

		//Swarm Follow target particles:
		swarmParticles.forEach((particle) => {
			particle.spaces.organicLinesStart();

			let rand = Math.random();
			if (rand <= 0.2) particle.position.followParticle = targetParticles[0];
			else if (rand <= 0.4) particle.position.followParticle = targetParticles[1];
			else if (rand <= 0.6) particle.position.followParticle = targetParticles[2];
			else if (rand <= 0.8) particle.position.followParticle = targetParticles[3];
			else particle.position.followParticle = targetParticles[4];

			particle.position.followRadius = followRadiusMax + r * mobileScaleBumpMax;
			particle.particleProps.sprite.blendMode = 'add';
		});

		targetParticles[0].animatedProps.setEase({
			x: 20,
			y: 10
		});

		targetParticles[1].animatedProps.setEase({
			x: 20,
			y: 10
		});

		targetParticles[2].animatedProps.setEase({
			x: 20,
			y: 10
		});

		targetParticles[3].animatedProps.setEase({
			x: 20,
			y: 10
		});

		targetParticles[4].animatedProps.setEase({
			x: 20,
			y: 10
		});

		targetParticles[0].deactivateAllBehaviours(true);
		targetParticles[0].activate();
		targetParticles[0].position.set({ x: sizes.xc, y: sizes.yc });

		targetParticles[0].animatedProps.targetAlpha = 0.8;
		targetParticles[0].animatedProps.targetScale = 0.18;
		targetParticles[0].particleProps.sprite.blendMode = 'add';

		targetParticles[1].deactivateAllBehaviours(true);
		targetParticles[1].activate();
		targetParticles[1].position.set({ x: sizes.xc, y: sizes.yc });

		targetParticles[1].animatedProps.targetAlpha = 0.8;
		targetParticles[1].animatedProps.targetScale = 0.18;
		targetParticles[1].particleProps.sprite.blendMode = 'add';

		targetParticles[2].deactivateAllBehaviours(true);
		targetParticles[2].activate();
		targetParticles[2].position.set({ x: sizes.xc, y: sizes.yc });

		targetParticles[2].animatedProps.targetAlpha = 0.8;
		targetParticles[2].animatedProps.targetScale = 0.18;
		targetParticles[2].particleProps.sprite.blendMode = 'add';

		targetParticles[3].deactivateAllBehaviours(true);
		targetParticles[3].activate();
		targetParticles[3].position.set({ x: sizes.xc, y: sizes.yc });

		targetParticles[3].animatedProps.targetAlpha = 0.8;
		targetParticles[3].animatedProps.targetScale = 0.18;
		targetParticles[3].particleProps.sprite.blendMode = 'add';

		targetParticles[4].deactivateAllBehaviours(true);
		targetParticles[4].activate();
		targetParticles[4].position.set({ x: sizes.xc, y: sizes.yc });

		targetParticles[4].animatedProps.targetAlpha = 0.8;
		targetParticles[4].animatedProps.targetScale = 0.18;
		targetParticles[4].particleProps.sprite.blendMode = 'add';

		targetParticles.forEach((particle) => {
			let baseScale = 0.2;
			particle.animatedProps.startBreathing(
				baseScale,
				0.05 * baseScale,
				1 * baseScale,
				[-1, 1],
				Math.random() * 0.4 * baseScale,
				Math.random() < 0.5 ? -1 : 1
			);
		});



		this._comboCounter.add(() => {
			r = 1 - Math.min(1, device.innerWidth / 1000);
			swarmParticles.forEach((particle) => {
				particle.position.followRadius = -1 * followRadiusMax - r * mobileScaleBumpMax;
			});
		}, 2000);

		this._comboCounter.add(() => {
			swarmParticles.forEach((particle) => {
				particle.position.followRadius = 0;
			});
		}, 3000);

		this._comboCounter.add(() => {
			swarmParticles.forEach((particle) => {
			});
		}, 4000);

		this._comboCounter.add(() => {
			r = 1 - Math.min(1, device.innerWidth / 1000);
			swarmParticles.forEach((particle) => {

				particle.animatedProps.targetAlpha = 0.3;
			});

			targetParticles.forEach((particle) => {
				particle.particlePhysics.active = true;
				particle.particlePhysics.drop(false, 0.02);
			});
		}, 4500);

		this._comboCounter.add(() => {
			swarmParticles.forEach((particle) => {

				particle.animatedProps.targetAlpha = 0;
				particle.animatedProps.targetScale = 0;
			});
		}, 5000);
	}

	private _organicLines(): void {
		let totalTargets = 5;
		let targetParticles = this._spacesParticles.slice(0, totalTargets);
		let swarmParticles = this._spacesParticles.slice(totalTargets);

		let sizes = this._getFrameSizes();

		let followRadiusMax = 0.025;
		let mobileScaleBumpMax = 0.05;
		let r = 1 - Math.min(1, device.innerWidth / 1000);

		//Swarm Follow target particles:
		swarmParticles.forEach((particle) => {
			particle.spaces.organicLinesStart();

			let rand = Math.random();
			if (rand <= 0.2) particle.position.followParticle = targetParticles[0];
			else if (rand <= 0.4) particle.position.followParticle = targetParticles[1];
			else if (rand <= 0.6) particle.position.followParticle = targetParticles[2];
			else if (rand <= 0.8) particle.position.followParticle = targetParticles[3];
			else particle.position.followParticle = targetParticles[4];

			particle.position.followRadius = followRadiusMax + r * mobileScaleBumpMax;
			particle.particleProps.sprite.blendMode = 'add';
		});

		targetParticles[0].animatedProps.setEase({
			x: 20,
			y: 10
		});

		targetParticles[1].animatedProps.setEase({
			x: 20,
			y: 10
		});

		targetParticles[2].animatedProps.setEase({
			x: 20,
			y: 10
		});

		targetParticles[3].animatedProps.setEase({
			x: 20,
			y: 10
		});

		targetParticles[4].animatedProps.setEase({
			x: 20,
			y: 10
		});

		targetParticles[0].deactivateAllBehaviours(true);
		targetParticles[0].activate();
		targetParticles[0].position.set({ x: sizes.xc, y: sizes.yc });

		targetParticles[0].animatedProps.targetAlpha = 0.8;
		targetParticles[0].animatedProps.targetScale = 0.18;
		targetParticles[0].particleProps.sprite.blendMode = 'add';

		targetParticles[1].deactivateAllBehaviours(true);
		targetParticles[1].activate();
		targetParticles[1].position.set({ x: sizes.xc, y: sizes.yc });

		targetParticles[1].animatedProps.targetAlpha = 0.8;
		targetParticles[1].animatedProps.targetScale = 0.18;
		targetParticles[1].particleProps.sprite.blendMode = 'add';

		targetParticles[2].deactivateAllBehaviours(true);
		targetParticles[2].activate();
		targetParticles[2].position.set({ x: sizes.xc, y: sizes.yc });

		targetParticles[2].animatedProps.targetAlpha = 0.8;
		targetParticles[2].animatedProps.targetScale = 0.18;
		targetParticles[2].particleProps.sprite.blendMode = 'add';

		targetParticles[3].deactivateAllBehaviours(true);
		targetParticles[3].activate();
		targetParticles[3].position.set({ x: sizes.xc, y: sizes.yc });

		targetParticles[3].animatedProps.targetAlpha = 0.8;
		targetParticles[3].animatedProps.targetScale = 0.18;
		targetParticles[3].particleProps.sprite.blendMode = 'add';

		targetParticles[4].deactivateAllBehaviours(true);
		targetParticles[4].activate();
		targetParticles[4].position.set({ x: sizes.xc, y: sizes.yc });

		targetParticles[4].animatedProps.targetAlpha = 0.8;
		targetParticles[4].animatedProps.targetScale = 0.18;
		targetParticles[4].particleProps.sprite.blendMode = 'add';

		targetParticles.forEach((particle) => {
			let baseScale = 0.2;
			particle.animatedProps.startBreathing(
				baseScale,
				0.05 * baseScale,
				1 * baseScale,
				[-1, 1],
				Math.random() * 0.4 * baseScale,
				Math.random() < 0.5 ? -1 : 1
			);
		});

		let xq = sizes.width / 4;
		let hq = sizes.height / 4;


		this._comboCounter.add(() => {
			let pos1 = { x: sizes.xc + xq / 1, y: sizes.yc + hq / 1 };
			let pos2 = { x: sizes.xc + xq / 1, y: sizes.yc - hq / 1 };
			let pos3 = { x: sizes.xc - xq / 1, y: sizes.yc - hq / 1 };
			let pos4 = { x: sizes.xc - xq / 1, y: sizes.yc + hq / 1 };

			targetParticles[0].position.set(pos1);
			targetParticles[1].position.set(pos2);
			targetParticles[2].position.set(pos3);
			targetParticles[3].position.set(pos4);
			targetParticles[4].position.set(pos4);

			swarmParticles.forEach((particle) => {
				particle.position.followRadius = followRadiusMax / 4 + r * (mobileScaleBumpMax / 4);
			});
		}, 2500);

		this._comboCounter.add(() => {
			let pos1 = { x: sizes.xc, y: sizes.yc + hq / 1 };
			let pos2 = { x: sizes.xc + xq / 1, y: sizes.yc };
			let pos3 = { x: sizes.xc, y: sizes.yc - hq / 1 };
			let pos4 = { x: sizes.xc - xq / 1, y: sizes.yc };
			let pos5 = { x: sizes.xc, y: sizes.yc };

			targetParticles[0].position.set(pos5);
			targetParticles[1].position.set(pos4);
			targetParticles[2].position.set(pos1);
			targetParticles[3].position.set(pos2);
			targetParticles[4].position.set(pos3);

			swarmParticles.forEach((particle) => {
				particle.position.followRadius = followRadiusMax / 5 + r * (mobileScaleBumpMax / 5);
			});
		}, 5000);

		this._comboCounter.add(() => {
			let pos1 = { x: sizes.xc, y: sizes.yc };

			targetParticles[0].position.set(pos1);
			targetParticles[1].position.set(pos1);
			targetParticles[2].position.set(pos1);
			targetParticles[3].position.set(pos1);
			targetParticles[4].position.set(pos1);

			swarmParticles.forEach((particle) => {
				particle.position.followRadius = followRadiusMax + r * mobileScaleBumpMax;
			});
		}, 7500);

		this._comboCounter.add(() => {
			swarmParticles.forEach((particle) => {
				particle.position.followRadius = 0;
			});
		}, 8500);

		this._comboCounter.add(() => {
			swarmParticles.forEach((particle) => {
				particle.position.followParticle = null;
			});
		}, 9500);
	}

	private _spacesBasicGrid(): void {
		let box = this._sceneProps.zonePositions.box;

		this._homeField.reposition(
			ParticleLayoutType.GRID,
			{ x: -1 * (this._sceneProps.zonePositions.box.w / 2), y: box.y },
			{ w: box.w, h: box.h },
			null,
			this._spacesParticles,
			1000
		);

		this._spacesParticles.forEach((particle) => particle.spaces.basicGrid());
	}

	/** Do perspective grid with standard layering */
	private _lineDropThenPhysicsBurst() {
		this._setPerspectiveArea();

		let particleIndex = 0;

		//Do 10 levels of depth
		let depthItems = 8;
		let cols = 9;
		let rows = 7;
		let colSpacing = this._spacesBoxPerspectiveArea.h / (cols - 1);
		let rowSpacing = this._spacesBoxPerspectiveArea.w / (rows - 1);
		let startX = -1 * (this._sceneProps.zonePositions.box.w / 2);
		let startY = this._spacesBoxPerspectiveArea.center.y - this._spacesBoxPerspectiveArea.h / 2;

		for (let depth = 1; depth <= depthItems; depth++) {
			if (depth >= 1) {
				let y = startY;
				let x = startX;
				let depths = [null, 1, 2, 3, 4, 5, 6, 7, 8, 9];

				for (let i = 0; i < cols; i++) {
					for (let j = 0; j < rows; j++) {
						let particle = this._spacesParticles[particleIndex];
						particle.spaces.lineDrop(depths[depth], { x: x, y: y }, this._spacesBoxPerspectiveArea, i, j, false);
						x += rowSpacing;
						particleIndex++;
					}

					y += colSpacing;
					x = startX;
				}
			}
		}

		//Hide the reset of the spaces particles:
		for (let i = particleIndex; i < this._spacesParticles.length; i++) {
			this._spacesParticles[i].hideMe();
		}
	}

	private _xIncrease = 5;
	private _maxSineX = 10;
	private _minSineX = -10;
	private _sineDirection = 1;
	private _sineYChange = 0.13;
	private _sinePlaySeed = 2.5;
	private _xCrestPointer = 0;
	private _xPointerDirection = 1;
	private _roundedIndex: number;
	private _total: number;
	private _sineType = 'random';
	private _frameSizes;

	private _processSineWave(): void {
		//Reuse one frame-sizes object (hot path: every frame while sine runs);
		//other _getFrameSizes callers still get fresh objects.
		this._frameSizes = this._getFrameSizes(this._frameSizes);

		this._sineIndex += (window as any).deltaTime * this._sinePlaySeed;

		this._xIncrease += (window as any).deltaTime * this._sineYChange * this._sineDirection;


		if (this._xIncrease > this._maxSineX || this._xIncrease < this._minSineX) {
			this._sineDirection *= -1;
		}

		if (this._xCrestPointer > 1 || this._xCrestPointer < 0) {
			this._xPointerDirection *= -1;
		}

		if (this._sineIndex >= this._sineLookup.length) {
			this._sineIndex = 0;
		}

		this._total = this._spaceSineParticles.length;
		this._roundedIndex = Math.round(this._sineIndex);

		//Plain indexed loop: forEach(fn.bind(this)) allocated a closure every
		//frame on this hot path (profiled at 6.1% of busy time under drag).
		for (let i = 0; i < this._total; i++) {
			this._processSineParticle(this._spaceSineParticles[i], i);
		}
	}

	private _processSineParticle(particle, index) {
		let transformedIndex = this._roundedIndex + Math.round(this._sineLookup.length * (index / this._total));
		if (transformedIndex >= this._sineLookup.length) transformedIndex -= this._sineLookup.length;

		if (!this._sineLookup[transformedIndex]) {
			return;
		}

		let rawX = this._screensIsometricGridPositions[index].x;



		let xRatio = rawX / 1.4;
		if (xRatio > 1) xRatio = 1;
		else if (xRatio < 0) xRatio = 0;

		let xYIncrease = 1;
		let scaleA = 2;

		if (this._sineType == 'random') {
			let yThing = this._sineLookup[Math.floor(xRatio * this._sineLookup.length)].y;
			xYIncrease = yThing * 1 * this._xIncrease;
		} else if (this._sineType == 'wavey') {
			xYIncrease = rawX * this._xIncrease;
			scaleA = 0.7;
		}

		let yEffect =
			this._homeField.getRelativeHeight(this._sineLookup[transformedIndex].y) * (3 + xYIncrease) * particle.sineSeed;
		let scale = 0.15 + yEffect * scaleA;


		if (scale < 0.1) scale = 0.1;

		if (this._screensIsometricGridPositions[index]) {
			particle.position.y = this._screensIsometricGridPositions[index].y + yEffect * -1;
			particle.animatedProps.hardSetScale(scale);

			particle.position.x =
				this._frameSizes.x1 +
				this._screensIsometricGridPositions[index].x * ((this._frameSizes.x2 - this._frameSizes.x1) * 0.75);
		}
	}
}
